// src/components/AdminDashboard.tsx
import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Sidebar } from './layout/sidebar';
import { Header } from './layout/header';
import { Loading } from './layout/loading';
import { useGlobalStore } from '@/lib/store';
import { ProtectedRoute } from './guards/ProtectedRoute';

// Pages
import Dashboard from '@/pages/dashboard/page';
import EventsPage from '@/pages/events/page';
import DonationsPage from '@/pages/donations/page';
import ActivitiesPage from '@/pages/activities/page';
import PartnersPage from '@/pages/partners/page';
import ContactsPage from '@/pages/contacts/page';
import PostersPage from '@/pages/posters/page';
import SettingsPage from '@/pages/settings/page';
import BlogsPage from '@/pages/blogs/page';
import BlogEditorPage from '@/pages/blogs/editor/page';
import MediaPage from '@/pages/media/page';

import TransactionsPage from '@/pages/transactions/page';
import MembersPage from '@/pages/members/page';

// Store Pages
import StoreProductsPage from '@/pages/store/products';
import StoreProductEditorPage from '@/pages/store/products/[id]';
import StoreOrdersPage from '@/pages/store/orders';
import StoreOrderDetailPage from '@/pages/store/orders/[id]';

// RBAC Pages
import RolesPage from '@/pages/roles';
import RoleEditorPage from '@/pages/roles/[id]';
import UsersPage from '@/pages/users';
import ForbiddenPage from '@/pages/403';

export default function AdminDashboard() {
  const [isMounted, setIsMounted] = useState(false);
  const sidebarOpen = useGlobalStore((state) => state.sidebarOpen);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return <Loading />;
  }

  return (
    <div className="h-screen bg-background">
      <div className="flex h-full">
        <Sidebar />
        <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
          <Header />
          <main className="p-4 sm:p-6 overflow-y-auto h-[calc(100vh-4rem)]">
            <Routes>
              {/* Public/Dashboard Route */}
              <Route path="/" element={<Dashboard />} />

              {/* 403 Forbidden Page */}
              <Route path="/403" element={<ForbiddenPage />} />

              {/* Protected Routes */}
              <Route path="/transactions" element={
                <ProtectedRoute resource="transactions">
                  <TransactionsPage />
                </ProtectedRoute>
              } />
              <Route path="/members" element={
                <ProtectedRoute resource="members">
                  <MembersPage />
                </ProtectedRoute>
              } />
              <Route path="/events" element={
                <ProtectedRoute resource="events">
                  <EventsPage />
                </ProtectedRoute>
              } />
              <Route path="/donations" element={
                <ProtectedRoute resource="donations">
                  <DonationsPage />
                </ProtectedRoute>
              } />
              <Route path="/activities" element={
                <ProtectedRoute resource="activities">
                  <ActivitiesPage />
                </ProtectedRoute>
              } />
              <Route path="/partners" element={
                <ProtectedRoute resource="partners">
                  <PartnersPage />
                </ProtectedRoute>
              } />
              <Route path="/contacts" element={
                <ProtectedRoute resource="contacts">
                  <ContactsPage />
                </ProtectedRoute>
              } />
              <Route path="/posters" element={
                <ProtectedRoute resource="posters">
                  <PostersPage />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/blogs" element={
                <ProtectedRoute resource="blogs">
                  <BlogsPage />
                </ProtectedRoute>
              } />
              <Route path="/blogs/new" element={
                <ProtectedRoute resource="blogs" action="create">
                  <BlogEditorPage />
                </ProtectedRoute>
              } />
              <Route path="/blogs/:id/edit" element={
                <ProtectedRoute resource="blogs" action="update">
                  <BlogEditorPage />
                </ProtectedRoute>
              } />
              <Route path="/media" element={
                <ProtectedRoute resource="media">
                  <MediaPage />
                </ProtectedRoute>
              } />

              {/* Store Routes */}
              <Route path="/store/products" element={
                <ProtectedRoute resource="products">
                  <StoreProductsPage />
                </ProtectedRoute>
              } />
              <Route path="/store/products/new" element={
                <ProtectedRoute resource="products" action="create">
                  <StoreProductEditorPage />
                </ProtectedRoute>
              } />
              <Route path="/store/products/:id" element={
                <ProtectedRoute resource="products" action="update">
                  <StoreProductEditorPage />
                </ProtectedRoute>
              } />
              <Route path="/store/orders" element={
                <ProtectedRoute resource="orders">
                  <StoreOrdersPage />
                </ProtectedRoute>
              } />
              <Route path="/store/orders/:id" element={
                <ProtectedRoute resource="orders">
                  <StoreOrderDetailPage />
                </ProtectedRoute>
              } />

              {/* RBAC Routes */}
              <Route path="/roles" element={
                <ProtectedRoute resource="roles">
                  <RolesPage />
                </ProtectedRoute>
              } />
              <Route path="/roles/:id" element={
                <ProtectedRoute resource="roles" action="update">
                  <RoleEditorPage />
                </ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute resource="users">
                  <UsersPage />
                </ProtectedRoute>
              } />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}